import React, { useState, useEffect } from 'react';
import { Link, router } from '@inertiajs/react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable, type ColumnProps } from '@/components/ui/data-table';
import { formatDistanceToNow } from 'date-fns';
import { Mail, Send, Edit, Eye, Loader2, Users } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';

interface Campaign {
  id: number;
  subject: string;
  status: 'draft' | 'sending' | 'sent';
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  sent_at: string | null;
  recipient_filter?: {
    roles?: string[];
    categories?: string[];
  };
}

interface Props {
  campaigns: Campaign[];
}

export default function CampaignsIndex({ campaigns }: Props) {
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  );
  const [isSending, setIsSending] = useState(false);

  // Auto-refresh when campaigns are sending
  useEffect(() => {
    const hasSendingCampaigns = campaigns.some((c) => c.status === 'sending');

    if (hasSendingCampaigns) {
      const interval = setInterval(() => {
        // Refresh the page data without full page reload
        router.reload({
          only: ['campaigns'],
          preserveState: true,
          preserveScroll: true,
        });
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [campaigns]);

  const getStatusBadge = (status: Campaign['status']) => {
    const variants = {
      draft: 'secondary',
      sending: 'default',
      sent: 'success',
    } as const;

    if (status === 'sending') {
      return (
        <Badge variant="default" className="flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Sending
        </Badge>
      );
    }

    return (
      <Badge variant={variants[status] || 'secondary'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleSendCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setSendDialogOpen(true);
  };

  const handleConfirmSend = () => {
    if (!selectedCampaign) return;

    setIsSending(true);
    router.post(
      `/admin/campaigns/${selectedCampaign.id}/send_campaign`,
      {},
      {
        onSuccess: () => {
          setSendDialogOpen(false);
          setSelectedCampaign(null);
        },
        onFinish: () => {
          setIsSending(false);
        },
      }
    );
  };

  const columns: ColumnProps<Campaign>[] = [
    {
      header: 'Subject',
      render: (campaign) => (
        <div className="py-2">
          <p className="font-medium text-gray-900">{campaign.subject}</p>
          {campaign.sent_at && (
            <p className="text-sm text-gray-500 mt-1">
              Sent{' '}
              {formatDistanceToNow(new Date(campaign.sent_at), {
                addSuffix: true,
              })}
            </p>
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      render: (campaign) => (
        <div className="py-2">{getStatusBadge(campaign.status)}</div>
      ),
    },
    {
      header: 'Recipients',
      render: (campaign) => (
        <span className="font-medium py-2 block">
          {campaign.recipient_count || '-'}
        </span>
      ),
    },
    {
      header: 'Delivered',
      render: (campaign) => {
        if (campaign.status === 'draft') {
          return <span className="text-gray-400 py-2 block">-</span>;
        }
        return (
          <div className="py-2">
            <span className="text-green-600 font-medium">
              {campaign.sent_count || 0}
            </span>
            {campaign.failed_count > 0 && (
              <span className="text-red-600 ml-1">
                ({campaign.failed_count} failed)
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: 'Created',
      render: (campaign) => (
        <span className="text-gray-500 py-2 block">
          {formatDistanceToNow(new Date(campaign.created_at), {
            addSuffix: true,
          })}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (campaign) => (
        <div className="flex gap-2 justify-end py-2">
          {campaign.status === 'draft' && (
            <>
              <Link href={`/admin/campaigns/${campaign.id}/edit`}>
                <Button size="sm" variant="outline" title="Edit">
                  <Edit className="h-4 w-4" />
                </Button>
              </Link>
              <Button
                size="sm"
                onClick={() => handleSendCampaign(campaign)}
                title="Send Campaign"
              >
                <Send className="h-4 w-4" />
              </Button>
            </>
          )}
          {campaign.status === 'sending' && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Processing...
            </span>
          )}
          <Link href={`/admin/campaigns/${campaign.id}`}>
            <Button size="sm" variant="outline" title="View Details">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout title="Campaigns">
      <div className="min-h-screen">
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Email Campaigns
                </h1>
                <p className="text-gray-600 mt-2">
                  Create and manage email campaigns to communicate with your users
                </p>
              </div>
              <Link href="/admin/campaigns/new">
                <Button size="lg" className="shadow-sm">
                  <Mail className="mr-2 h-5 w-5" />
                  New Campaign
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="space-y-6">
            {/* Campaign Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                        Total Campaigns
                      </p>
                      <p className="text-4xl font-light">{campaigns.length}</p>
                      <div className="h-1 w-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" />
                    </div>
                    <Mail className="h-5 w-5 text-purple-500 mt-1" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                        Sent Campaigns
                      </p>
                      <p className="text-4xl font-light">
                        {campaigns.filter((c) => c.status === 'sent').length}
                      </p>
                      <div className="h-1 w-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full" />
                    </div>
                    <Send className="h-5 w-5 text-green-500 mt-1" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                        Total Recipients
                      </p>
                      <p className="text-4xl font-light">
                        {campaigns.reduce(
                          (sum, c) => sum + (c.sent_count || 0),
                          0
                        )}
                      </p>
                      <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" />
                    </div>
                    <Users className="h-5 w-5 text-blue-500 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Auto-refresh indicator */}
            {campaigns.some((c) => c.status === 'sending') && (
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Sending in progress
                    </p>
                    <p className="text-xs text-gray-600">
                      Auto-refreshing every 3 seconds
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => router.reload()}
                  className="text-blue-600 hover:text-blue-700 hover:bg-white/50"
                >
                  Refresh Now
                </Button>
              </div>
            )}

            {campaigns.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-16 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <Mail className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">
                    No campaigns yet
                  </h3>
                  <p className="text-gray-500 max-w-sm mx-auto mb-8">
                    Create your first email campaign to start communicating with
                    your users
                  </p>
                  <Link href="/admin/campaigns/new">
                    <Button size="lg" className="shadow-sm">
                      <Mail className="mr-2 h-5 w-5" />
                      Create Your First Campaign
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-sm pt-0 pb-6">
                <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-medium">
                        Campaign History
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        {campaigns.length}{' '}
                        {campaigns.length === 1 ? 'campaign' : 'campaigns'}{' '}
                        created
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <DataTable
                    data={campaigns}
                    columns={columns}
                    emptyMessage="No campaigns found"
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Send Confirmation Dialog */}
        <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Campaign</DialogTitle>
              <DialogDescription>
                Are you sure you want to send this campaign?
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4">
              <div>
                <p className="text-sm text-gray-500">Campaign Subject:</p>
                <p className="font-medium">{selectedCampaign?.subject}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Target Audience:</p>
                {selectedCampaign && (
                  <div className="mt-1">
                    {selectedCampaign.recipient_filter?.roles && (
                      <p className="text-gray-900 font-medium">
                        {selectedCampaign.recipient_filter.roles
                          .map(
                            (r) => r.charAt(0).toUpperCase() + r.slice(1) + 's'
                          )
                          .join(' and ')}
                      </p>
                    )}
                    {selectedCampaign.recipient_filter?.categories && (
                      <p className="text-gray-900 font-medium">
                        {selectedCampaign.recipient_filter.categories
                          .map((c) =>
                            c
                              .replace('-', ' ')
                              .split(' ')
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(' ')
                          )
                          .join(' and ')}{' '}
                        category
                      </p>
                    )}
                    {!selectedCampaign.recipient_filter?.roles &&
                      !selectedCampaign.recipient_filter?.categories && (
                        <p className="text-gray-900 font-medium">
                          All verified users
                        </p>
                      )}
                    {selectedCampaign.recipient_count > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        Total: {selectedCampaign.recipient_count}{' '}
                        {selectedCampaign.recipient_count === 1
                          ? 'user'
                          : 'users'}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Important:</strong> This action cannot be undone. The
                  campaign will be sent immediately to all selected recipients.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setSendDialogOpen(false)}
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button onClick={handleConfirmSend} disabled={isSending}>
                {isSending ? 'Sending...' : 'Send Campaign'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
